import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../auth/AdminAuthContext";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      await login(email, password);
      const redirect = (location.state as { from?: string } | null)?.from || "/admin/dashboard";
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background:
          "radial-gradient(circle at top left, rgba(181, 143, 102, 0.18), transparent 28%), radial-gradient(circle at bottom right, rgba(110, 74, 47, 0.2), transparent 24%), linear-gradient(180deg, #2d2119 0%, #1d1611 100%)",
        px: 2
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          p: { xs: 3, md: 4 },
          borderRadius: 5,
          background: "rgba(255, 250, 244, 0.96)",
          border: "1px solid rgba(214, 189, 161, 0.32)",
          boxShadow: "0 30px 100px rgba(29, 22, 17, 0.28)"
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.28em", textTransform: "uppercase", color: "#87674c" }}>
              Eventena
            </Typography>
            <Typography variant="h3" sx={{ mt: 1, fontWeight: 800, lineHeight: 1.05, fontSize: { xs: "1.85rem", md: "2.1rem" } }}>
              Sign in to the admin console
            </Typography>
            <Typography sx={{ mt: 1.2, color: "text.secondary" }}>
              Access quotation operations, customer workflows, reporting, and print-ready proposal management.
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            fullWidth
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            sx={{
              py: 1.4,
              borderRadius: 14,
              background: "linear-gradient(135deg, #8a5a3c 0%, #6b442d 100%)"
            }}
          >
            {submitting ? "Signing in..." : "Sign in to Admin"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
